import {
  useLayoutEffect,
  useRef,
  useState,
  type ComponentProps,
  type KeyboardEvent,
  type ReactElement,
} from "react";
import { ChevronDown, Pencil, Plus, X } from "lucide-react";
import {
  widgetTargetKey,
  type FunnelLevel,
  type FunnelParams,
} from "@playbookai/widget-schemas";
import { Button, StatusDot, Textarea } from "../../atoms/index.js";
import type { CategoricalTint } from "../../lib/tints.js";
import { cn } from "../../lib/utils.js";
import { TruncatedText } from "../../molecules/TruncatedText/index.js";
import type { WidgetBodyProps } from "../types.js";
import { useFieldEditSession } from "../field-edit-session.js";

/**
 * Funnel Analysis — Look, multi. Three stacked level bands that NARROW
 * top→bottom (broad → actionable), with a ChevronDown connector between
 * bands — mirroring the funnel shape. Tones use DS tokens (level 3 = pb-red).
 * Ported from the oracle FunnelBody (workspace/look/FunnelCard.tsx).
 */

/** Width fractions: broad → narrowest, giving the funnel its taper. */
const WIDTH_FRACS = [1, 0.78, 0.58];

const LEVEL_TONES: Array<{
  tint: CategoricalTint;
  border: string;
  bg: string;
  pillBg: string;
  pillText: string;
  heading: string;
  chip: string;
}> = [
  // Funnel stages are CATEGORIES, so they take the categorical tint ramp
  // (ADR-0092) — the 68%-lightness steps 5/7/3, which clear AA as text on the
  // dark theme — rather than semantic status hues. One alpha per role in every
  // theme; the `dark:` variant is not how this theme system flips.
  {
    tint: 5,
    border: "border-pb-tint-5/40",
    bg: "bg-pb-tint-5/10",
    pillBg: "bg-pb-tint-5/15",
    pillText: "text-pb-tint-5",
    heading: "text-pb-tint-5",
    chip: "bg-pb-tint-5/10 text-pb-text border-pb-tint-5/30",
  },
  {
    tint: 7,
    border: "border-pb-tint-7/40",
    bg: "bg-pb-tint-7/10",
    pillBg: "bg-pb-tint-7/15",
    pillText: "text-pb-tint-7",
    heading: "text-pb-tint-7",
    chip: "bg-pb-tint-7/10 text-pb-text border-pb-tint-7/30",
  },
  {
    tint: 3,
    border: "border-pb-tint-3/40",
    bg: "bg-pb-tint-3/10",
    pillBg: "bg-pb-tint-3/15",
    pillText: "text-pb-tint-3",
    heading: "text-pb-tint-3",
    chip: "bg-pb-tint-3/10 text-pb-text border-pb-tint-3/30",
  },
];

type EditingItem = {
  readonly levelId: string;
  readonly itemId: string;
  readonly draft: string;
};

export function FunnelWidget({
  data,
  expanded = false,
  onLevelTitleChange,
  onItemTextChange,
  onAddItem,
  onRemoveItem,
  readOnly,
}: WidgetBodyProps<FunnelParams> & {
  readonly onLevelTitleChange?: (levelId: string, title: string) => void;
  readonly onItemTextChange?: (
    levelId: string,
    itemId: string,
    text: string,
  ) => void;
  readonly onAddItem?: (levelId: string, text: string) => void;
  readonly onRemoveItem?: (levelId: string, itemId: string) => void;
  readonly expanded?: boolean;
}): ReactElement {
  const [editingItem, setEditingItem] = useState<EditingItem | null>(null);
  const startEditingItem = (
    levelId: string,
    itemId: string,
    text: string,
  ): void => {
    if (readOnly || !onItemTextChange) return;
    setEditingItem({ levelId, itemId, draft: text });
  };
  const updateEditingDraft = (draft: string): void => {
    setEditingItem((current) => (current ? { ...current, draft } : current));
  };
  const cancelEditingItem = (): void => setEditingItem(null);
  const confirmEditingItem = (): void => {
    const text = editingItem?.draft.trim();
    if (readOnly || !onItemTextChange || !editingItem || !text) return;
    onItemTextChange(editingItem.levelId, editingItem.itemId, text);
    setEditingItem(null);
  };
  const removeItem = (levelId: string, itemId: string): void => {
    if (!onRemoveItem) return;
    if (editingItem?.levelId === levelId && editingItem.itemId === itemId) {
      setEditingItem(null);
    }
    onRemoveItem(levelId, itemId);
  };

  if (!expanded) return <CompactFunnel data={data} />;

  return (
    <div className="@container flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto">
      {data.levels.map((level, idx) => {
        const tone = LEVEL_TONES[Math.min(idx, LEVEL_TONES.length - 1)]!;
        const isLast = idx === data.levels.length - 1;
        return (
          <div key={level.id} className="flex flex-col items-center">
            <div
              className={cn(
                "w-full min-w-0",
                idx === 1 && "@3xl:w-[85%]",
                idx === 2 && "@3xl:w-[68%]",
              )}
              data-testid={`funnel-level-${level.id}`}
            >
              <Level
                level={level}
                position={idx + 1}
                tone={tone}
                readOnly={readOnly}
                onTitleChange={onLevelTitleChange}
                onItemTextChange={onItemTextChange}
                editingItem={
                  editingItem?.levelId === level.id ? editingItem : null
                }
                onStartEditingItem={startEditingItem}
                onEditingDraftChange={updateEditingDraft}
                onCancelEditingItem={cancelEditingItem}
                onConfirmEditingItem={confirmEditingItem}
                onAddItem={onAddItem}
                onRemoveItem={onRemoveItem ? removeItem : undefined}
                expanded
              />
            </div>
            {isLast ? null : (
              <ChevronDown
                className={cn("w-4 h-4 my-1 opacity-70", tone.pillText)}
                aria-hidden
              />
            )}
          </div>
        );
      })}
    </div>
  );
}

function CompactFunnel({
  data,
}: {
  readonly data: FunnelParams;
}): ReactElement {
  return (
    <div className="flex min-h-0 flex-1 flex-col justify-center overflow-hidden">
      {data.levels.map((level, idx) => {
        const tone = LEVEL_TONES[Math.min(idx, LEVEL_TONES.length - 1)]!;
        const widthFrac = WIDTH_FRACS[idx] ?? 0.5;
        const isLast = idx === data.levels.length - 1;
        return (
          <div key={level.id} className="flex flex-col items-center">
            <div
              className="w-full"
              data-testid={`funnel-level-${level.id}`}
              style={{ maxWidth: `${Math.round(widthFrac * 100)}%` }}
            >
              <div
                aria-label={`${level.title}: ${formatItemCount(level.items.length)}`}
                className={cn(
                  "flex min-h-12 items-center justify-between gap-2 rounded-lg border px-3 py-2",
                  level.items.length === 0 && "border-dashed opacity-75",
                  tone.border,
                  tone.bg,
                )}
              >
                <TruncatedText
                  className={cn(
                    "min-w-0 flex-1 text-xs font-bold",
                    tone.heading,
                  )}
                >
                  {level.title}
                </TruncatedText>
                <span
                  data-testid={`funnel-level-count-${level.id}`}
                  className={cn(
                    "shrink-0 rounded-md px-1.5 py-0.5 text-dense-2 font-semibold uppercase tracking-wider",
                    tone.pillBg,
                    tone.pillText,
                  )}
                >
                  {formatItemCount(level.items.length)}
                </span>
              </div>
            </div>
            {isLast ? null : (
              <ChevronDown
                className={cn("my-0.5 h-3.5 w-3.5 opacity-70", tone.pillText)}
                aria-hidden
              />
            )}
          </div>
        );
      })}
    </div>
  );
}

function formatItemCount(count: number): string {
  return `${count} item${count === 1 ? "" : "s"}`;
}

function Level({
  level,
  position,
  tone,
  readOnly,
  onTitleChange,
  onItemTextChange,
  editingItem,
  onStartEditingItem,
  onEditingDraftChange,
  onCancelEditingItem,
  onConfirmEditingItem,
  onAddItem,
  onRemoveItem,
  expanded,
}: {
  level: FunnelLevel;
  position: number;
  tone: (typeof LEVEL_TONES)[number];
  readOnly?: boolean | undefined;
  onTitleChange?: ((levelId: string, title: string) => void) | undefined;
  onItemTextChange?:
    | ((levelId: string, itemId: string, text: string) => void)
    | undefined;
  editingItem?: EditingItem | null | undefined;
  onStartEditingItem?:
    | ((levelId: string, itemId: string, text: string) => void)
    | undefined;
  onEditingDraftChange?: ((draft: string) => void) | undefined;
  onCancelEditingItem?: (() => void) | undefined;
  onConfirmEditingItem?: (() => void) | undefined;
  onAddItem?: ((levelId: string, text: string) => void) | undefined;
  onRemoveItem?: ((levelId: string, itemId: string) => void) | undefined;
  expanded?: boolean | undefined;
}): ReactElement {
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState("");
  /*
   * PB-4923 AC5. The level's own title, plus one target per item beneath it.
   * `NewItemComposer` deliberately leases NOTHING: its item has no id until it
   * is confirmed, so a claim there would name a target no operation can
   * address. The funnel's root `title` field has no control on this surface at
   * all, so there is nothing to lease for it either.
   */
  const titleLease = useFieldEditSession(
    widgetTargetKey([
      { kind: "item", collection: "levels", itemId: level.id },
      { kind: "prop", name: "title" },
    ]),
  );
  const itemTarget = (itemId: string): string =>
    widgetTargetKey([
      { kind: "item", collection: "levels", itemId: level.id },
      { kind: "item", collection: "items", itemId },
      { kind: "prop", name: "text" },
    ]);
  const startAdding = (): void => {
    if (readOnly || level.items.length >= 50) return;
    setAdding(true);
  };
  const cancelAdding = (): void => {
    setAdding(false);
    setDraft("");
  };
  const confirmAdding = (): void => {
    const text = draft.trim();
    if (readOnly || !onAddItem || !text || level.items.length >= 50) return;
    onAddItem(level.id, text);
    cancelAdding();
  };
  // 3.625rem = 58px — the oracle band min-height (raw px is banned by the DS lint).
  return (
    <div
      className={cn(
        "rounded-lg border p-4 flex flex-col gap-3",
        tone.border,
        tone.bg,
      )}
    >
      <div className="flex flex-wrap items-center justify-between gap-2">
        {onTitleChange ? (
          // eslint-disable-next-line ds/no-hand-built -- kept: a bare 14px bold title field in the level's tint; Input ghost adds a hover wash and a 1px edge in plain ink, and no approved look row lists the change.
          <input
            aria-label={`Level ${position} title`}
            value={level.title}
            disabled={readOnly}
            {...titleLease}
            onChange={(event) => onTitleChange(level.id, event.target.value)}
            className={cn(
              "min-w-0 flex-1 basis-full @lg:basis-0 truncate rounded-md border-0 bg-transparent p-0 text-sm font-bold focus-field",
              tone.heading,
            )}
          />
        ) : (
          <span
            className={cn(
              "min-w-0 flex-1 basis-full @lg:basis-0 break-words text-sm font-bold",
              tone.heading,
            )}
          >
            {level.title}
          </span>
        )}
        <span
          className={cn(
            "text-dense-2 font-semibold uppercase tracking-wider py-0.5 px-1.5 rounded-md",
            tone.pillBg,
            tone.pillText,
          )}
        >
          {level.items.length} item{level.items.length === 1 ? "" : "s"}
        </span>
        {onAddItem && !readOnly && !adding ? (
          <AddItemButton
            levelTitle={level.title}
            tone={tone}
            readOnly={level.items.length >= 50}
            onClick={startAdding}
          />
        ) : null}
      </div>
      {level.items.length === 0 && !adding ? (
        <div className="flex items-center gap-2">
          <span className="text-xs text-pb-text-muted italic">
            No items yet
          </span>
        </div>
      ) : (
        <div
          className={cn(
            "grid grid-cols-1 gap-3",
            position === 1 && "@3xl:grid-cols-3",
            position === 2 && "@3xl:grid-cols-2",
          )}
        >
          {level.items.map((it, itemIndex) =>
            expanded ? (
              editingItem?.itemId === it.id ? (
                <ItemEditor
                  key={it.id}
                  levelTitle={level.title}
                  itemIndex={itemIndex}
                  value={editingItem.draft}
                  disabled={readOnly}
                  tone={tone}
                  targetKey={itemTarget(it.id)}
                  onChange={onEditingDraftChange ?? (() => {})}
                  onCancel={onCancelEditingItem ?? (() => {})}
                  onConfirm={onConfirmEditingItem ?? (() => {})}
                />
              ) : (
                <ItemCard
                  key={it.id}
                  levelTitle={level.title}
                  itemIndex={itemIndex}
                  text={it.text}
                  tone={tone}
                  readOnly={readOnly}
                  onEdit={
                    onItemTextChange && onStartEditingItem
                      ? () => onStartEditingItem(level.id, it.id, it.text)
                      : undefined
                  }
                  onRemove={
                    onRemoveItem
                      ? () => onRemoveItem(level.id, it.id)
                      : undefined
                  }
                />
              )
            ) : (
              <span
                key={it.id}
                className={cn(
                  "text-xs font-medium px-2 py-0.5 rounded-full border",
                  tone.chip,
                )}
              >
                {it.text}
              </span>
            ),
          )}
          {adding ? (
            <NewItemComposer
              levelTitle={level.title}
              position={position}
              value={draft}
              disabled={readOnly}
              onChange={setDraft}
              onCancel={cancelAdding}
              onConfirm={confirmAdding}
            />
          ) : null}
        </div>
      )}
    </div>
  );
}

function ItemCard({
  levelTitle,
  itemIndex,
  text,
  tone,
  readOnly,
  onEdit,
  onRemove,
}: {
  readonly levelTitle: string;
  readonly itemIndex: number;
  readonly text: string;
  readonly tone: (typeof LEVEL_TONES)[number];
  readonly readOnly?: boolean | undefined;
  readonly onEdit?: (() => void) | undefined;
  readonly onRemove?: (() => void) | undefined;
}): ReactElement {
  const itemNumber = itemIndex + 1;
  const hasActions = Boolean(!readOnly && (onEdit || onRemove));
  return (
    <div
      className={cn(
        "group/item relative min-w-0 rounded-md border p-3",
        tone.chip,
      )}
    >
      <p
        className={cn(
          "whitespace-pre-wrap break-words text-sm leading-relaxed [overflow-wrap:anywhere]",
          hasActions && "pe-16",
        )}
      >
        {text}
      </p>
      {hasActions ? (
        <div className="absolute end-1.5 top-1.5 flex opacity-0 transition-opacity group-hover/item:opacity-100 group-focus-within/item:opacity-100">
          {onEdit ? (
            <Button
              variant="ghost"
              size="sm"
              iconOnly
              aria-label={`Edit ${levelTitle} item ${itemNumber}`}
              onClick={onEdit}
              className="text-pb-text-muted"
            >
              <Pencil className="h-3.5 w-3.5" />
            </Button>
          ) : null}
          {onRemove ? (
            <Button
              variant="ghost"
              size="sm"
              iconOnly
              aria-label={`Delete ${levelTitle} item ${itemNumber}: ${text}`}
              onClick={onRemove}
              className="rounded text-pb-text-muted transition-colors hover:bg-pb-red/10 hover:text-pb-red-text focus-ring"
            >
              <X className="h-3.5 w-3.5" />
            </Button>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

function ItemEditor({
  levelTitle,
  itemIndex,
  value,
  disabled,
  tone,
  targetKey,
  onChange,
  onCancel,
  onConfirm,
}: {
  readonly levelTitle: string;
  readonly itemIndex: number;
  readonly value: string;
  readonly disabled?: boolean | undefined;
  readonly tone: (typeof LEVEL_TONES)[number];
  /** PB-4923 AC5 — the merge-map leaf this editor holds while it is open. */
  readonly targetKey: string;
  readonly onChange: (value: string) => void;
  readonly onCancel: () => void;
  readonly onConfirm: () => void;
}): ReactElement {
  const itemNumber = itemIndex + 1;
  const lease = useFieldEditSession(targetKey);
  return (
    <div className={cn("rounded-md border p-2", tone.chip)}>
      <AutoGrowingTextarea
        aria-label={`Edit ${levelTitle} item ${itemNumber}`}
        value={value}
        disabled={disabled}
        focusOnMount
        showCount
        {...lease}
        onChange={onChange}
        onCancel={onCancel}
        onSubmit={onConfirm}
      />
      <div className="mt-1 flex justify-end gap-2">
        <Button
          variant="ghost"
          size="sm"
          aria-label={`Cancel editing ${levelTitle} item ${itemNumber}`}
          onClick={onCancel}
        >
          Cancel
        </Button>
        <Button
          variant="primary"
          size="sm"
          disabled={disabled || !value.trim()}
          aria-label={`Save ${levelTitle} item ${itemNumber}`}
          onClick={onConfirm}
        >
          Save
        </Button>
      </div>
    </div>
  );
}

function AddItemButton({
  levelTitle,
  tone,
  readOnly,
  onClick,
}: {
  readonly levelTitle: string;
  readonly tone: (typeof LEVEL_TONES)[number];
  readonly readOnly?: boolean | undefined;
  readonly onClick: () => void;
}): ReactElement {
  // The level's colour rides a small swatch; the button itself is neutral
  // (look row OK-07).
  return (
    <Button
      variant="ghost"
      size="sm"
      aria-label={`Add item to ${levelTitle}`}
      disabled={readOnly}
      onClick={onClick}
      className="shrink-0"
    >
      <StatusDot tint={tone.tint} />
      <Plus className="h-3.5 w-3.5" /> Add item
    </Button>
  );
}

function NewItemComposer({
  levelTitle,
  position,
  value,
  disabled,
  onChange,
  onCancel,
  onConfirm,
}: {
  readonly levelTitle: string;
  readonly position: number;
  readonly value: string;
  readonly disabled?: boolean | undefined;
  readonly onChange: (value: string) => void;
  readonly onCancel: () => void;
  readonly onConfirm: () => void;
}): ReactElement {
  return (
    <div className="col-span-full rounded-md border border-pb-control-border bg-pb-card/80 p-2">
      <AutoGrowingTextarea
        aria-label={`New item for ${levelTitle}`}
        value={value}
        disabled={disabled}
        placeholder={itemPlaceholder(position)}
        showCount
        onChange={onChange}
        onCancel={onCancel}
        onSubmit={onConfirm}
      />
      <div className="mt-1 flex justify-end gap-2">
        <Button
          variant="ghost"
          size="sm"
          aria-label={`Cancel adding item to ${levelTitle}`}
          onClick={onCancel}
        >
          Cancel
        </Button>
        <Button
          variant="primary"
          size="sm"
          disabled={disabled || !value.trim()}
          aria-label={`Confirm add item to ${levelTitle}`}
          onClick={onConfirm}
        >
          Add item
        </Button>
      </div>
    </div>
  );
}

function AutoGrowingTextarea({
  value,
  onChange,
  onSubmit,
  onCancel,
  focusOnMount,
  className,
  ...props
}: {
  readonly value: string;
  readonly onChange: (value: string) => void;
  readonly onSubmit?: (() => void) | undefined;
  readonly onCancel?: (() => void) | undefined;
  readonly focusOnMount?: boolean | undefined;
  readonly className?: string | undefined;
} & Omit<
  ComponentProps<typeof Textarea>,
  "value" | "onChange" | "className" | "variant" | "size"
>): ReactElement {
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  useLayoutEffect(() => resizeTextarea(textareaRef.current), [value]);
  useLayoutEffect(() => {
    if (focusOnMount) textareaRef.current?.focus();
  }, [focusOnMount]);
  const handleKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>): void => {
    if (event.key === "Escape" && onCancel) {
      event.preventDefault();
      onCancel();
      return;
    }
    if (event.key === "Enter" && (event.metaKey || event.ctrlKey) && onSubmit) {
      event.preventDefault();
      onSubmit();
    }
  };
  return (
    <Textarea
      {...props}
      ref={textareaRef}
      rows={2}
      maxLength={500}
      value={value}
      onChange={(event) => {
        resizeTextarea(event.currentTarget);
        onChange(event.target.value);
      }}
      onKeyDown={handleKeyDown}
      className={cn("min-h-16 resize-none overflow-hidden", className)}
    />
  );
}

function resizeTextarea(element: HTMLTextAreaElement | null): void {
  if (!element) return;
  element.style.height = "auto";
  element.style.height = `${element.scrollHeight}px`;
}

function itemPlaceholder(position: number): string {
  if (position === 1) return "Describe an observation or candidate…";
  if (position === 2) return "Explain why this could be happening…";
  return "Describe the actionable root cause and supporting evidence…";
}
